import { useMutation } from '@apollo/react-hooks'
import { Fab, Grow, makeStyles, styled } from '@material-ui/core'
import CompanyIcon from '@material-ui/icons/LocationCity'
import SaveIcon from '@material-ui/icons/Save'
import PasswordIcon from '@material-ui/icons/VpnKey'
import { useUpdateClientMutation, useUpdateUserMutation, useUserQuery } from '@paintscout/api'
import { removeTypenames } from '@paintscout/util/util'
import type { Auth0UserWithClaims, ProfileFormikValues } from '@paintscout/ui'
import {
  Button,
  ClientOptionsProvider,
  ConfirmationDialog,
  DialogStackContext,
  DialogStackPortal,
  Hidden,
  PopupMenuItem,
  ProfileFormik,
  Spinner,
  Tab,
  Tabs,
  Typography
} from '@paintscout/ui'
import { gql } from 'apollo-boost'
import { Form } from 'formik'
import { useSnackbar } from 'notistack'
import type { Auth0User, OptionsDocument, UserPreferencesDocument } from 'paintscout'
import React, { useContext, useState } from 'react'
import type { RouteComponentProps } from 'react-router-dom'
import { Link } from 'react-router-dom'
import LaunchDarkly from 'shared/components/common/LaunchDarkly'
import ErrorMessage from '../../components/ErrorMessage'
import NavHeader from '../../components/NavDrawer/NavHeader'
import ResetPasswordDialog from '../../components/ResetPasswordDialog'
import UserAdministrationForm from '../../components/UserAdministrationForm'
import UserProfilePage from '../../components/UserProfilePage'

const CompanyLink = styled(Link)(() => ({
  display: 'flex',
  alignItems: 'center',
  textDecoration: 'none',
  fontWeight: 'inherit',
  color: 'inherit'
}))

const useStyles = makeStyles({
  tabsWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  mobileSaveButton: {
    position: 'fixed',
    bottom: 16,
    right: 16
  },
  title: {
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center'
  },
  chip: {}
})

export default function EditUser({ match }: RouteComponentProps<{ id: string; section: string }>) {
  const classes = useStyles({})
  const id = decodeURIComponent(match.params.id)
  const { enqueueSnackbar } = useSnackbar()
  const [section, setSection] = useState(match.params.section || 'administration')
  const [launchDarklyReady, setLaunchDarklyReady] = useState(false)

  const [updateUser] = useUpdateUserMutation()
  const [updateClient] = useUpdateClientMutation()
  const { data, loading, error, refetch } = useUserQuery({
    variables: {
      id
    },
    fetchPolicy: 'network-only'
  })

  const user = data?.user
  const preferences = { ...user?.preferences, presentation: user?.preferences?.presentation || { description: '' } }
  const clientMeta = data?.user?.client?.meta
  const options: OptionsDocument = data?.user?.client?.options

  if (error) {
    return <ErrorMessage>{error.message}</ErrorMessage>
  }

  if (loading || !user) {
    return <Spinner fullWidth fullHeight />
  }

  async function handleSaveOptions({ options }: { options: OptionsDocument }) {
    try {
      const res = await updateClient({
        variables: {
          id: clientMeta._id,
          options
        }
      })

      const updatedOptions = res?.data?.updateClient?.options ?? null
      if (!updatedOptions) {
        enqueueSnackbar('Unable to save options', { variant: 'error' })
      } else {
        await refetch()
        return {
          options: updatedOptions
        }
      }
    } catch (error) {
      console.error(error)

      enqueueSnackbar('Unable to save options', { variant: 'error' })
      return { options }
    }
  }

  async function handleSave({
    user,
    preferences,
    options
  }: {
    user: Auth0UserWithClaims
    preferences: UserPreferencesDocument
    options: OptionsDocument
  }) {
    try {
      await updateUser({
        variables: {
          input: removeTypenames({
            user_id: user.user_id,
            email: user.email,
            user_metadata: user.user_metadata,
            app_metadata: user.app_metadata,
            preferences
          })
        }
      })
      await updateClient({
        variables: {
          id: clientMeta._id,
          options
        }
      })
      enqueueSnackbar('Saved', { variant: 'success' })
    } catch (e) {
      console.error(e)

      enqueueSnackbar('Unable to save user', { variant: 'error' })
    }
  }

  return (
    <LaunchDarkly
      onReady={() => setLaunchDarklyReady(true)}
      userDetails={{
        id: user.user_id,
        companyId: id,
        parentCompanyId: clientMeta?.parentCompanyId || null,
        email: user.email
      }}
    >
      {!launchDarklyReady ? (
        <Spinner />
      ) : (
        <ClientOptionsProvider options={options} clientId={clientMeta._id} onSave={handleSaveOptions} isAdmin={true}>
          <DialogStackPortal />
          <ProfileFormik
            user={user as Auth0User}
            preferences={preferences}
            options={options}
            onSubmit={async (values, actions) => {
              await handleSave(values)
              actions.resetForm({ values })
            }}
          >
            {({ isSubmitting, values, dirty }) => {
              return (
                <Form>
                  <UserNavHeader values={values} />
                  <Typography variant="h3" gutterBottom>
                    <CompanyLink to={`/clients/${clientMeta._id}`}>
                      <CompanyIcon style={{ marginBottom: 4, marginRight: 4 }} />
                      <span>{options.options.companyName.value}</span>
                    </CompanyLink>
                  </Typography>
                  <div className={classes.tabsWrapper}>
                    <Tabs value={section} onChange={(ev, value) => setSection(value)} style={{ marginBottom: 20 }}>
                      <Tab label="Administration" value="administration" />
                      <Tab label="Profile" value="profile" />
                    </Tabs>
                    <Hidden mdDown>
                      <Grow in={dirty}>
                        <Button loading={isSubmitting} icon={SaveIcon} type="submit">
                          Save
                        </Button>
                      </Grow>
                    </Hidden>
                  </div>

                  {section === 'profile' && <UserProfilePage />}
                  {section === 'administration' && <UserAdministrationForm />}
                  <Hidden lgUp>
                    <div className={classes.mobileSaveButton}>
                      <Grow in={dirty}>
                        <Fab disabled={!dirty || isSubmitting} color="primary" type="submit">
                          <SaveIcon />
                        </Fab>
                      </Grow>
                    </div>
                  </Hidden>
                </Form>
              )
            }}
          </ProfileFormik>
        </ClientOptionsProvider>
      )}
    </LaunchDarkly>
  )
}

function UserNavHeader({ values }: { values: ProfileFormikValues }) {
  const { enqueueSnackbar } = useSnackbar()
  const { openDialog, dismissDialog, dismissAllDialogs } = useContext(DialogStackContext)
  const classes = useStyles({})

  const [requestPassword] = useMutation(gql`
    mutation resetPassword($user_id: String!) {
      resetPassword(user_id: $user_id)
    }
  `)

  async function handleResetPasswordEmail() {
    openDialog(ConfirmationDialog, {
      message: `This well send an email to "${values.user.email}" to reset their password.`,
      onConfirm: async () => {
        try {
          await requestPassword({ variables: { user_id: values.user.user_id } })

          enqueueSnackbar('Sent Password Reset email', { variant: 'success' })
        } catch (e) {
          console.error(e)
          enqueueSnackbar('Failed to send Password Reset email', { variant: 'error' })
        }
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  const [updatePassword] = useMutation(gql`
    mutation updatePassword($user_id: String!, $password: String!) {
      updatePassword(user_id: $user_id, password: $password)
    }
  `)

  const handleResetPasswordDialog = () => {
    openDialog(ResetPasswordDialog, {
      onConfirm: (password: string) => {
        openDialog(ConfirmationDialog, {
          message: `Are you sure you want to reset your password?`,
          onConfirm: async () => {
            try {
              await updatePassword({ variables: { user_id: values.user.user_id, password } })
              enqueueSnackbar('Password updated', { variant: 'success' })
              dismissAllDialogs()
            } catch (error) {
              console.error(error)
              enqueueSnackbar('Failed to reset password', { variant: 'error' })
              dismissDialog()
            }
          },
          onCancel: dismissDialog
        })
      },
      onCancel: dismissDialog
    })
  }

  const menuItems = (
    <>
      <PopupMenuItem onClick={handleResetPasswordEmail} icon={PasswordIcon}>
        Send Password Reset Email
      </PopupMenuItem>
      <PopupMenuItem onClick={handleResetPasswordDialog} icon={PasswordIcon}>
        Manually Reset Password
      </PopupMenuItem>
    </>
  )

  return (
    <NavHeader
      title={
        <div className={classes.title}>
          <Typography
            variant={'h1'}
          >{`${values.user.user_metadata.firstName} ${values.user.user_metadata.lastName}`}</Typography>
        </div>
      }
      showBackButton
      menuItems={menuItems}
    />
  )
}
